import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import approvalsService, { PendingApproval, ApprovalFilters } from '../../services/approvals';

interface ApprovalsState {
  items: PendingApproval[];
  selectedIds: Set<string>;
  processingIds: Set<string>;
  currentPage: number;
  totalPages: number;
  total: number;
  filters: ApprovalFilters;
  loading: boolean;
  error: string | null;
}

const initialState: ApprovalsState = {
  items: [],
  selectedIds: new Set(),
  processingIds: new Set(),
  currentPage: 1,
  totalPages: 1,
  total: 0,
  filters: {},
  loading: false,
  error: null,
};

// Async thunks
export const fetchApprovals = createAsyncThunk(
  'approvals/fetch',
  async ({ page, pageSize = 20, filters }: { page: number; pageSize?: number; filters?: ApprovalFilters }) => {
    const response = await approvalsService.getPendingApprovals(page, pageSize, filters);
    return response;
  }
);

export const approveImage = createAsyncThunk(
  'approvals/approve',
  async ({ approvalId, platforms }: { approvalId: string; platforms: string[] }) => {
    await approvalsService.approve({ approval_id: approvalId, platforms });
    return approvalId;
  }
);

export const rejectImage = createAsyncThunk(
  'approvals/reject',
  async ({ approvalId, reason }: { approvalId: string; reason: string }) => {
    await approvalsService.reject({ approval_id: approvalId, reason });
    return approvalId;
  }
);

export const batchApprove = createAsyncThunk(
  'approvals/batchApprove',
  async ({ approvalIds, platforms }: { approvalIds: string[]; platforms: string[] }) => {
    await approvalsService.batchApprove(approvalIds, platforms);
    return approvalIds;
  }
);

export const batchReject = createAsyncThunk(
  'approvals/batchReject',
  async ({ approvalIds, reason }: { approvalIds: string[]; reason: string }) => {
    await approvalsService.batchReject(approvalIds, reason);
    return approvalIds;
  }
);

const approvalsSlice = createSlice({
  name: 'approvals',
  initialState,
  reducers: {
    toggleSelection: (state, action: PayloadAction<string>) => {
      const newSet = new Set(state.selectedIds);
      if (newSet.has(action.payload)) {
        newSet.delete(action.payload);
      } else {
        newSet.add(action.payload);
      }
      state.selectedIds = newSet;
    },
    selectAll: (state) => {
      state.selectedIds = new Set(state.items.map(item => item.id));
    },
    clearSelection: (state) => {
      state.selectedIds = new Set();
    },
    setFilters: (state, action: PayloadAction<ApprovalFilters>) => {
      state.filters = action.payload;
      state.currentPage = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch approvals
    builder
      .addCase(fetchApprovals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApprovals.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
        state.totalPages = Math.ceil(action.payload.total / 20);
        state.selectedIds = new Set();
      })
      .addCase(fetchApprovals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch approvals';
      })
      // Approve image
      .addCase(approveImage.pending, (state, action) => {
        state.processingIds.add(action.meta.arg.approvalId);
      })
      .addCase(approveImage.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item.id !== action.payload);
        state.processingIds.delete(action.payload);
        state.selectedIds.delete(action.payload);
      })
      .addCase(approveImage.rejected, (state, action) => {
        state.processingIds.delete(action.meta.arg.approvalId);
      })
      // Reject image
      .addCase(rejectImage.pending, (state, action) => {
        state.processingIds.add(action.meta.arg.approvalId);
      })
      .addCase(rejectImage.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item.id !== action.payload);
        state.processingIds.delete(action.payload);
        state.selectedIds.delete(action.payload);
      })
      .addCase(rejectImage.rejected, (state, action) => {
        state.processingIds.delete(action.meta.arg.approvalId);
      })
      // Batch approve
      .addCase(batchApprove.fulfilled, (state, action) => {
        const approvedSet = new Set(action.payload);
        state.items = state.items.filter(item => !approvedSet.has(item.id));
        state.selectedIds = new Set();
      })
      // Batch reject
      .addCase(batchReject.fulfilled, (state, action) => {
        const rejectedSet = new Set(action.payload);
        state.items = state.items.filter(item => !rejectedSet.has(item.id));
        state.selectedIds = new Set();
      });
  },
});

export const { toggleSelection, selectAll, clearSelection, setFilters, setPage } = approvalsSlice.actions;
export default approvalsSlice.reducer;